"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  Star,
  CalendarCheck,
  MapPin,
  Award,
  Users,
  Clock,
  ThumbsUp,
} from "lucide-react";

function useInView<T extends HTMLElement>(threshold = 0.2) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, inView } = useInView<HTMLDivElement>(0.15);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        transitionProperty: "opacity, transform",
        transitionDuration: "700ms",
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
        transitionDelay: `${delay}ms`,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(16px)",
      }}
    >
      {children}
    </div>
  );
}

function AnimatedNumber({
  value,
  suffix = "",
  decimals = 0,
  duration = 1400,
}: {
  value: number;
  suffix?: string;
  decimals?: number;
  duration?: number;
}) {
  const { ref, inView } = useInView<HTMLSpanElement>(0.4);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start: number | null = null;
    let frame: number;

    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        setDisplay(value);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, value, duration]);

  return (
    <span ref={ref}>
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const AboutPage = () => {
  return (
    <main className="bg-white overflow-x-hidden">
      {/* ── HERO BANNER ── */}
      <section className="relative text-white py-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/images/about.png"
            alt="About background"
            fill
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-block mb-4 text-xs font-bold uppercase tracking-widest text-blue-200 bg-blue-900/40 px-4 py-1.5 rounded-full border border-blue-400/30 backdrop-blur-sm">
            Who We Are
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-white drop-shadow-lg">
            About Embrace Rentals
          </h1>
          <p className="mt-6 text-base text-white/90 max-w-2xl mx-auto leading-relaxed">
            A fully registered Malawian rental business committed to
            professionalism, transparency, and putting customers first — every
            single time.
          </p>
        </div>
      </section>

      {/* ── OUR STORY ── */}
      <section className="px-6 py-12 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-video bg-gray-100">
              <video
                src="/videos/about.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-blue-700">
                Our Story
              </span>
              <h2 className="mt-2 text-3xl font-extrabold text-blue-900 leading-tight">
                Built on Trust,
                <br />
                Driven by Service
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed text-sm">
                <strong className="text-slate-900">Embrace Rentals</strong> was
                founded with a simple belief — that renting a car in Malawi
                should be easy, affordable, and completely transparent. No
                hidden fees, no surprises.
              </p>
              <p className="mt-3 text-gray-600 leading-relaxed text-sm">
                Based in Blantyre, we proudly serve customers across Malawi,
                offering a carefully maintained fleet for personal travel,
                business trips, and everything in between.
              </p>

              <div className="mt-5 p-4 rounded-2xl bg-blue-50 border border-blue-100 space-y-2.5">
                <p className="font-bold text-blue-900 text-xs uppercase tracking-wide mb-3">
                  Official Registration
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Award className="w-4 h-4 text-blue-700 shrink-0" />
                  <span>
                    <strong>Reg No:</strong> BRN-V6SYMG6
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <CalendarCheck className="w-4 h-4 text-blue-700 shrink-0" />
                  <span>
                    <strong>Registered:</strong> 29 July 2025
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-blue-700 shrink-0" />
                  <span>
                    <strong>Location:</strong> Blantyre Urban, Malawi
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── BY THE NUMBERS ── */}
      <section className="px-6 py-10 text-black">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            {
              icon: <Users className="w-5 h-5" />,
              value: 500,
              suffix: "+",
              decimals: 0,
              label: "Happy Customers",
            },
            {
              icon: <Star className="w-5 h-5" />,
              value: 4.9,
              suffix: "★",
              decimals: 1,
              label: "Average Rating",
            },
            {
              icon: <Clock className="w-5 h-5" />,
              value: null,
              staticValue: "24/7",
              label: "Support Available",
            },
            {
              icon: <ThumbsUp className="w-5 h-5" />,
              value: 100,
              suffix: "%",
              decimals: 0,
              label: "Verified Fleet",
            },
          ].map(({ icon, value, suffix, decimals, staticValue, label }, i) => (
            <Reveal key={label} delay={i * 100}>
              <div className="group flex flex-col items-center gap-2 p-3 rounded-2xl transition-transform duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-blue-50 text-blue-900 transition-transform duration-300 group-hover:scale-110">
                  {icon}
                </div>
                <p className="text-3xl font-extrabold text-blue-900">
                  {value !== null ? (
                    <AnimatedNumber
                      value={value}
                      suffix={suffix}
                      decimals={decimals}
                    />
                  ) : (
                    staticValue
                  )}
                </p>
                <p className="text-md sm:text-xs font-bold text-blue-700 uppercase tracking-wide">
                  {label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section className="px-6 py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-8">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-700">
                What Drives Us
              </span>
              <h2 className="mt-2 text-3xl font-extrabold text-blue-900">
                Mission &amp; Vision
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal delay={0}>
              <div className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm transition-shadow duration-300 hover:shadow-md h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-blue-800 text-white font-extrabold text-sm">
                    M
                  </div>
                  <h3 className="text-base font-extrabold text-slate-900">
                    Our Mission
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">
                  To provide safe, reliable, and affordable vehicle rentals to
                  individuals and businesses in Malawi, with outstanding
                  customer service at every step of the journey.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="p-6 rounded-2xl bg-blue-900 text-white shadow-sm transition-shadow duration-300 hover:shadow-md h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white text-blue-900 font-extrabold text-sm">
                    V
                  </div>
                  <h3 className="text-base font-extrabold">Our Vision</h3>
                </div>
                <p className="text-white/80 leading-relaxed text-sm">
                  To become Malawi&apos;s most trusted and accessible rental
                  platform — expanding our fleet, services, and reach to every
                  corner of the country and beyond.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ── */}
      <section className="px-6 py-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-8">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-700">
                What We Stand For
              </span>
              <h2 className="mt-2 text-3xl font-extrabold text-blue-900">
                Our Values
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            {[
              {
                icon: <ShieldCheck className="w-6 h-6 text-blue-700" />,
                bg: "bg-blue-50 group-hover:bg-blue-100",
                title: "Transparency",
                desc: "Clear pricing with zero hidden charges — ever.",
              },
              {
                icon: (
                  <Star className="w-6 h-6 text-amber-500 fill-amber-400" />
                ),
                bg: "bg-amber-50 group-hover:bg-amber-100",
                title: "Excellence",
                desc: "Top-rated service that consistently exceeds expectations.",
              },
              {
                icon: <CalendarCheck className="w-6 h-6 text-emerald-600" />,
                bg: "bg-emerald-50 group-hover:bg-emerald-100",
                title: "Reliability",
                desc: "Vehicles ready when you need them, no last-minute surprises.",
              },
              {
                icon: <Users className="w-6 h-6 text-blue-700" />,
                bg: "bg-blue-50 group-hover:bg-blue-100",
                title: "Customer First",
                desc: "Your experience drives every decision we make.",
              },
            ].map(({ icon, bg, title, desc }, i) => (
              <Reveal key={title} delay={i * 90}>
                <div className="group flex flex-col items-center text-center p-5 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div
                    className={`mb-4 flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-300 group-hover:scale-110 ${bg}`}
                  >
                    {icon}
                  </div>
                  <h3 className="text-sm font-extrabold text-gray-900">
                    {title}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                    {desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CUSTOMER REVIEW ── */}
      <section className="px-6 py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <>
              <span className="text-xs font-bold uppercase tracking-widest text-blue-700">
                What Customers Say
              </span>
              <h2 className="mt-2 text-3xl font-extrabold text-blue-900 mb-6">
                Real Reviews
              </h2>

              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm text-left transition-shadow duration-300 hover:shadow-md">
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 text-amber-400 fill-amber-400"
                      />
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-gray-700">
                    5.0
                  </span>
                  <span className="text-xs text-gray-400">
                    · Verified review
                  </span>
                </div>

                <blockquote className="pl-4 border-l-2 border-blue-100">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    &ldquo;Great service and excellent car! The booking process
                    was simple and the car was in perfect condition. I will
                    definitely use Embrace Rentals again. Highly
                    recommended!&rdquo;
                  </p>
                  <footer className="mt-4 flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                      S
                    </div>
                    <span className="text-xs font-semibold text-gray-800">
                      Sarah M.
                    </span>
                    <span className="text-[10px] text-gray-400">
                      · Verified customer
                    </span>
                  </footer>
                </blockquote>
              </div>
            </>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="px-6 py-12 text-blue-900 text-center">
        <Reveal>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-extrabold mb-3 text-blue-900">
              Ready to Hit the Road?
            </h2>
            <p className="text-blue-800 text-sm mb-8 leading-relaxed">
              Browse our fleet and book your vehicle in minutes. No hidden fees,
              no paperwork hassles — just great cars and great service.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/vehicles">
                <button className="px-6 py-3 rounded-xl bg-white text-blue-900 text-sm font-extrabold hover:bg-blue-50 active:scale-95 transition-all shadow">
                  Explore Our Vehicles
                </button>
              </Link>
              <Link href="/contact">
                <button className="px-6 py-3 rounded-xl bg-white text-blue-900 text-sm font-extrabold hover:bg-blue-50 active:scale-95 transition-all shadow">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default AboutPage;
